import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import { ChevronUpIcon, ChevronDownIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { getErrorTypeDisplay, extractCoreErrorMessage } from '../../utils/errorTypeParser';

interface ErrorTypeStats {
  error_type: string;
  error_type_display?: string;
  error_count: number;
  flow_count: number;
  last_error: string;
  most_common_error: string;
}

interface ErrorTypeFlow {
  flow_id: string;
  flow_definition_id: string;
  flow_name: string;
  error_count: number;
  unique_errors: number;
  last_error: string;
}

interface ErrorTypesListProps {
  orgId: string;
  onFlowClick?: (flowId: string) => void;
  expandedType?: string;
  className?: string;
}

export default function ErrorTypesList({ 
  orgId, 
  onFlowClick,
  expandedType,
  className = ''
}: ErrorTypesListProps) {
  const [errorTypes, setErrorTypes] = useState<ErrorTypeStats[]>([]);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [typeFlows, setTypeFlows] = useState<Record<string, ErrorTypeFlow[]>>({});
  const [loading, setLoading] = useState(true);
  const [loadingType, setLoadingType] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (orgId) {
      fetchErrorTypes();
    }
  }, [orgId]);

  useEffect(() => {
    if (expandedType && !expandedTypes.has(expandedType)) {
      toggleTypeExpansion(expandedType);
    }
  }, [expandedType]);

  const fetchErrorTypes = async () => {
    try {
      setLoading(true);
      
      // Try RPC function first
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_error_type_stats', {
        p_org_id: orgId,
        p_days: 30
      });

      if (!rpcError && rpcData && rpcData.length > 0) {
        // Check if RPC data has valid flow counts
        const hasValidFlowCounts = rpcData.some(item => item.flow_count > 0);
        if (hasValidFlowCounts) {
          console.log('Using RPC data for error types:', rpcData);
          setErrorTypes(rpcData);
          return;
        }
        console.log('RPC data has invalid flow counts, falling back to direct query');
      }

      // If RPC fails, fall back to direct query
      console.log('RPC failed for error types, falling back to direct query:', rpcError);
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      // Get all errors grouped by type
      const { data: errors, error: queryError } = await supabase
        .from('sf_flow_interview_errors')
        .select('error_type, error_type_display, flow_definition_id, created_date, error_message')
        .eq('org_id', orgId)
        .gte('created_date', thirtyDaysAgo.toISOString())
        .order('created_date', { ascending: false });

      if (queryError) {
        console.error('Direct query error:', queryError);
        return;
      }

      if (!errors || errors.length === 0) {
        setErrorTypes([]);
        return;
      }

      // Group by error type
      const typeMap = new Map();
      errors.forEach(error => {
        const type = error.error_type || 'OTHER';
        
        if (!typeMap.has(type)) {
          typeMap.set(type, {
            error_type: type,
            error_type_display: error.error_type_display || null,
            error_count: 0,
            flow_count: new Set(),
            last_error: error.created_date,
            most_common_error: error.error_message
          });
        } else {
          // Update display name if we find a non-null one
          const stats = typeMap.get(type);
          if (!stats.error_type_display && error.error_type_display) {
            stats.error_type_display = error.error_type_display;
          }
        }
        
        const stats = typeMap.get(type);
        stats.error_count++;
        stats.flow_count.add(error.flow_definition_id);
        
        if (new Date(error.created_date) > new Date(stats.last_error)) {
          stats.last_error = error.created_date;
        }
      });

      // Convert to array
      const errorTypes = Array.from(typeMap.values()).map(stats => ({
        error_type: stats.error_type,
        error_type_display: stats.error_type_display,
        error_count: stats.error_count,
        flow_count: stats.flow_count.size, // Convert Set size to number
        last_error: stats.last_error,
        most_common_error: stats.most_common_error
      }));
      

      // Sort by error count
      errorTypes.sort((a, b) => b.error_count - a.error_count);
      setErrorTypes(errorTypes);

    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTypeFlows = async (errorType: string) => {
    try {
      setLoadingType(errorType);
      
      // Try RPC function first
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_error_type_flows', {
        p_org_id: orgId,
        p_error_type: errorType,
        p_days: 30
      });

      if (!rpcError && rpcData) {
        setTypeFlows(prev => ({
          ...prev,
          [errorType]: rpcData
        }));
        return;
      }

      // If RPC fails, fall back to direct query
      console.log('RPC failed for type flows, falling back to direct query:', rpcError);
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      // Get errors for this type
      const { data: errors, error: queryError } = await supabase
        .from('sf_flow_interview_errors')
        .select('flow_definition_id, flow_name, created_date, error_message')
        .eq('org_id', orgId)
        .eq('error_type', errorType)
        .gte('created_date', thirtyDaysAgo.toISOString())
        .order('created_date', { ascending: false });

      if (queryError) {
        console.error('Direct query error for type flows:', queryError);
        return;
      }

      if (!errors || errors.length === 0) {
        setTypeFlows(prev => ({
          ...prev,
          [errorType]: []
        }));
        return;
      }

      // Get flow definitions to get proper flow IDs
      const flowDefIds = [...new Set(errors.map(e => e.flow_definition_id))];
      const { data: flowDefs, error: flowDefError } = await supabase
        .from('sf_flow_definitions')
        .select('id, active_version_id, master_label')
        .eq('org_id', orgId)
        .in('active_version_id', flowDefIds);

      const flowMap = new Map(flowDefs?.map(f => [f.active_version_id, f]) || []);

      // Group by flow
      const flowGroups = new Map();
      errors.forEach(error => {
        const flowDef = flowMap.get(error.flow_definition_id);
        const flowId = flowDef?.id || error.flow_definition_id;
        
        
        // Use flow name from sf_flow_definitions (which doesn't have timestamps)
        const flowName = flowDef?.master_label || error.flow_name || 'Unknown Flow';
        
        if (!flowGroups.has(flowId)) {
          flowGroups.set(flowId, {
            flow_id: flowId,
            flow_definition_id: error.flow_definition_id,
            flow_name: flowName,
            error_count: 0,
            unique_errors: new Set(),
            last_error: error.created_date
          });
        }
        
        const group = flowGroups.get(flowId);
        group.error_count++;
        group.unique_errors.add(error.error_message);
        
        if (new Date(error.created_date) > new Date(group.last_error)) {
          group.last_error = error.created_date;
        }
      });

      // Convert to array
      const typeFlowData = Array.from(flowGroups.values()).map(group => ({
        ...group,
        unique_errors: group.unique_errors.size
      }));

      // Sort by error count
      typeFlowData.sort((a, b) => b.error_count - a.error_count);

      setTypeFlows(prev => ({
        ...prev,
        [errorType]: typeFlowData
      }));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoadingType(null);
    }
  };

  const toggleTypeExpansion = async (errorType: string) => {
    const newExpanded = new Set(expandedTypes);
    
    if (newExpanded.has(errorType)) {
      newExpanded.delete(errorType);
    } else {
      newExpanded.add(errorType);
      // Fetch type flows if not already loaded
      if (!typeFlows[errorType]) {
        await fetchTypeFlows(errorType);
      }
    }
    
    setExpandedTypes(newExpanded);
  };

  const handleFlowClick = (flowId: string) => {
    if (onFlowClick) {
      onFlowClick(flowId);
    } else {
      navigate(`/${orgId}/flow/${flowId}`);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow ${className}`}>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Error Types</h3>
            <p className="text-sm text-gray-500">Most common flow error categories</p>
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            {isCollapsed ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            )}
          </button>
        </div>

        {!isCollapsed && (
          <div className="space-y-3">
            {errorTypes.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No flow errors found in the last 30 days
              </p>
            ) : (
              errorTypes.map((errorType) => (
                <div key={errorType.error_type} className="border rounded-lg">
                  <div
                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleTypeExpansion(errorType.error_type)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">
                            {errorType.error_type_display}
                          </span>
                          <div className="flex items-center space-x-4 text-sm">
                            <span className="text-red-600 font-medium">
                              {errorType.error_count} errors
                            </span>
                            <span className="text-gray-500">
                              {errorType.flow_count} flows
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTypeExpansion(errorType.error_type);
                              }}
                              className="text-blue-600 hover:text-blue-700 font-medium flex items-center"
                            >
                              View Flows
                              {expandedTypes.has(errorType.error_type) ? (
                                <ChevronDownIcon className="ml-1 h-4 w-4" />
                              ) : (
                                <ChevronUpIcon className="ml-1 h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          Last error: {formatDistanceToNow(new Date(errorType.last_error), { addSuffix: true })}
                          {errorType.most_common_error && (
                            <> • Common: {extractCoreErrorMessage(errorType.most_common_error)}</>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {expandedTypes.has(errorType.error_type) && (
                    <div className="border-t bg-gray-50 px-4 py-2">
                      {loadingType === errorType.error_type ? (
                        <div className="py-2 text-center">
                          <div className="inline-flex items-center text-sm text-gray-500">
                            <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading flows...
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {typeFlows[errorType.error_type] && typeFlows[errorType.error_type].length > 0 ? (
                            typeFlows[errorType.error_type].map((flow, index) => (
                            <div
                              key={`${flow.flow_id}-${index}`}
                              className="flex items-center justify-between py-2 hover:bg-gray-100 px-2 rounded cursor-pointer"
                              onClick={() => handleFlowClick(flow.flow_id)}
                            >
                              <div className="flex-1">
                                <span className="text-sm font-medium text-gray-700">
                                  {flow.flow_name}
                                </span>
                                <span className="ml-2 text-xs text-gray-500">
                                  {formatDistanceToNow(new Date(flow.last_error), { addSuffix: true })}
                                </span>
                                <div className="text-xs text-gray-600 mt-1">
                                  {flow.error_count} {flow.error_count === 1 ? 'error' : 'errors'}
                                  {flow.unique_errors > 1 && ` • ${flow.unique_errors} unique variations`}
                                </div>
                              </div>
                              <div className="flex items-center text-blue-600 hover:text-blue-700">
                                <span className="text-sm">View Flow</span>
                                <span className="ml-1">→</span>
                              </div>
                            </div>
                          ))
                          ) : (
                            <div className="py-3 text-center text-sm text-gray-500">
                              No flows found for this error type
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}